import type { ShopperSearch } from '@/scapi';
import { VENUE_FACET_ATTRIBUTE_ID } from './venue-facet';

export type FilterValue = NonNullable<ShopperSearch.schemas['ProductSearchRefinement']['values']>[0];

export type RefinementProps = {
    values: FilterValue[];
    attributeId: string;
    isFilterSelected: (attributeId: string, value: string) => boolean;
    toggleFilter: (attributeId: string, value: string) => void;
    isPending: boolean;
};

/** Attributes that accept only one `refine` value at a time (e.g. price range, category). */
export const EXCLUSIVE_REFINEMENT_ATTRIBUTE_IDS = ['price', VENUE_FACET_ATTRIBUTE_ID, 'cgid'] as const;

/** Route/API refinements hidden from the drawer. */
export const PLP_SYSTEM_REFINEMENT_IDS = new Set(['orderable_only']);

/**
 * Refinements omitted from the Filtra badge.
 * `cgid` stays in the drawer (as subcategories) but the PLP always applies one category refine.
 */
export const PLP_BADGE_EXCLUDED_REFINEMENT_IDS = new Set(['cgid', ...PLP_SYSTEM_REFINEMENT_IDS]);

export type UsePlpRefinementsParams = {
    result: {
        refinements?: ShopperSearch.schemas['ProductSearchResult']['refinements'];
        selectedRefinements?: ShopperSearch.schemas['ProductSearchResult']['selectedRefinements'];
        total?: number;
    };
    /** Current `refine` query values from the loader */
    refine?: string[];
    /** When true, refinements are ignored (e.g. content folder listings). */
    disabled?: boolean;
    /** Attribute ids hidden from the drawer and excluded from the filter badge count */
    excludeRefinementAttributeIds?: readonly string[];
    /** Route category. Used to resolve sibling values for the category filter. */
    categoryId?: string;
    /** When true, the drawer includes sibling categories of `categoryId`. */
    showCategoryFilter?: boolean;
};

export type UsePlpRefinementsReturn = {
    /** SCAPI facets for the drawer */
    refinements: ShopperSearch.schemas['ProductSearchRefinement'][];
    isPending: boolean;
    isFilterSelected: (attributeId: string, value: string) => boolean;
    hasActiveFilter: (attributeId: string) => boolean;
    toggleFilter: (attributeId: string, value: string) => void;
    /** Clears drawer refinements */
    clearAllRefinements: () => void;
    /** Whether the category has usable refinements */
    hasUsableRefinements: boolean;
    /** Number of active user filters (excludes cgid and orderable_only) */
    selectedFiltersCount: number;
};
